import { useState } from "react";
import { ALL_PAGES, roleBadge } from "@/components/PermissionEditor";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const levels = [
  { value: "edit", label: "View and edit", color: "text-emerald-700 dark:text-emerald-300" },
  { value: "read", label: "View only", color: "text-blue-700 dark:text-blue-300" },
  { value: "none", label: "No access", color: "text-slate-600 dark:text-slate-300" },
];

export function pageLevel(member, key) {
  // Settings and Billing are protected by OwnerRoute regardless of saved levels.
  if (key === "settings" || key === "billing") return "none";
  const permissions = member.permissions || {};
  if (Array.isArray(permissions)) return permissions.includes(key) ? "edit" : "none";
  return ["read", "edit"].includes(permissions[key]) ? permissions[key] : "none";
}

export default function CurrentTeam({ members, properties, loading, error, onRetry, onEdit, onRemove, removingId, onSavePhone }) {
  const [removeTarget, setRemoveTarget] = useState(null);
  const button = "min-h-[44px] px-3 py-2 rounded-lg text-sm font-semibold border transition";
  return (
    <section aria-label="Current team" className="space-y-3">
      <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Current team{!loading && !error ? ` (${members.length})` : ""}</h3>
      {loading ? <p role="status" className="text-sm text-slate-600 dark:text-slate-300">Loading team access…</p> : error ? (
        <div role="alert" className="text-sm text-red-700 dark:text-red-300">
          <p>Could not load your team. Try again to see current access.</p>
          <button type="button" onClick={onRetry} className={`${button} mt-2 border-red-300 dark:border-red-700`}>Retry</button>
        </div>
      ) : members.length === 0 ? (
        <p className="text-sm text-slate-600 dark:text-slate-300">No team members yet. Invite a co-host, manager or cleaner below.</p>
      ) : members.map(member => {
        const name = member.name || member.email || member.phone || "Team member";
        const assigned = member.assigned_property_ids || [];
        const revenue = member.show_revenue ?? (member.role === "co_host");
        return (
          <article key={member.staff_id} className="p-4 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/30 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <h4 className="text-sm font-semibold text-slate-900 dark:text-white break-words">{name}</h4>
                {member.name && member.email && <p className="text-xs text-slate-600 dark:text-slate-300 break-all">{member.email}</p>}
                <p className="text-xs text-slate-600 dark:text-slate-300">{roleBadge(member.role)} · {member.status === "active" ? "Active" : member.status === "pending" ? "Invite pending" : "Inactive"}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => onEdit(member)} className={`${button} border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/30`}>Edit access</button>
                <button type="button" onClick={() => setRemoveTarget(member)} disabled={!!removingId} className={`${button} border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50`}>{member.status === "pending" ? "Cancel invite" : "Remove from team"}</button>
              </div>
            </div>
            <dl className="space-y-2 text-sm text-slate-700 dark:text-slate-200">
              <div><dt className="font-semibold">Properties</dt><dd className="break-words">{assigned.length ? assigned.map(id => properties.find(p => (p.id || p.property_id) === id)?.name || "Unavailable property").join(", ") : "All properties"}</dd></div>
              <div><dt className="font-semibold">Revenue figures</dt><dd>{revenue ? "Visible" : "Hidden"}</dd></div>
            </dl>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-slate-200 dark:border-slate-600 pt-3">
              {levels.map(level => {
                const pages = ALL_PAGES.filter(page => pageLevel(member, page.key) === level.value);
                return (
                  <div key={level.value}>
                    <h5 className={`text-xs font-semibold ${level.color}`}>{level.label}</h5>
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">{pages.map(page => page.label).join(", ") || "None"}</p>
                  </div>
                );
              })}
            </div>
            {!revenue && pageLevel(member, "gst") !== "none" && <p className="text-xs text-slate-600 dark:text-slate-300">GST reports are unavailable while revenue figures are hidden.</p>}
            {member.status === "pending" && <p className="text-xs text-slate-600 dark:text-slate-300">This access starts after the invitation is accepted.</p>}
            <label className="block text-xs text-slate-600 dark:text-slate-300">
              WhatsApp number for booking and ID alerts
              <input key={`${member.staff_id}:${member.phone || ""}`} type="tel" defaultValue={member.phone || ""} placeholder="Add WhatsApp number"
                onBlur={e => { if (e.target.value.trim() !== (member.phone || "")) onSavePhone(member.staff_id, e.target.value); }}
                className="mt-1 block w-full sm:max-w-xs min-h-[44px] rounded-lg px-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-base text-slate-800 dark:text-slate-100" />
            </label>
          </article>
        );
      })}
      <p className="text-xs text-slate-600 dark:text-slate-300">Settings, billing and team management are host only. Page access and revenue sharing are controlled separately.</p>
      <AlertDialog open={!!removeTarget} onOpenChange={open => { if (!open && !removingId) setRemoveTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{removeTarget?.status === "pending" ? "Cancel this invitation?" : "Remove this team member?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget?.name || removeTarget?.email || removeTarget?.phone || "This person"} will lose access to your workspace. Their invitation and existing sessions will stop working. Your properties and bookings will stay in place.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!removingId}>Keep member</AlertDialogCancel>
            <AlertDialogAction disabled={!!removingId} className="bg-red-600 hover:bg-red-700 text-white min-h-[44px]"
              onClick={async e => {
                e.preventDefault();
                if (removeTarget && await onRemove(removeTarget.staff_id)) setRemoveTarget(null);
              }}>{removingId ? "Removing…" : removeTarget?.status === "pending" ? "Cancel invite" : "Remove from team"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
